import { Stack } from '@cast/design-system';
import {
  ChartMetricsRow,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  HpaChartsContent,
  useHpaChartData,
} from '@cast/split-me/features/cluster/workload-optimization';

import { InventoryResourceMetricBox } from '../../../_components/ui/InventoryResourceMetricBox';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

type HpaMetricKey =
  | 'currentPods'
  | 'podCountMin'
  | 'podCountMax'
  | 'cpuCoresMin'
  | 'cpuCoresMax';

const colorMap: Record<HpaMetricKey, string> = {
  currentPods: 'grey.200',
  podCountMin: 'grey.200',
  podCountMax: 'grey.200',
  cpuCoresMin: 'green.300',
  cpuCoresMax: 'green.300',
};

const titleMap: Record<HpaMetricKey, string> = {
  currentPods: 'CURRENT POD COUNT',
  podCountMin: 'MIN POD COUNT',
  podCountMax: 'MAX POD COUNT',
  cpuCoresMin: 'MIN CPU USAGE',
  cpuCoresMax: 'MAX CPU USAGE',
};

const HPA_METRIC_KEYS = Object.keys(titleMap) as HpaMetricKey[];

export const HpaCharts = () => {
  const timeRangeSelection = useInventoryWorkloadsTimeRangeSelection();
  const {
    metrics,
    workload,
    isFetching,
    error,
    refetch,
    podsChartData,
    cpuChartData,
    podsAnalyzeWithOpsPilot,
    cpuAnalyzeWithOpsPilot,
  } = useHpaChartData({
    timeRangeSelection,
  });

  const isLoading = isFetching || !workload;

  if (error && !isFetching) {
    return (
      <WidgetLayout title="Horizontal autoscaling" testId="hpa-charts">
        <FailedToLoad
          title="Failed to load horizontal autoscaling"
          refresh={refetch}
        />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout title="Horizontal autoscaling" testId="hpa-charts">
      <Stack gap={12} height="100%">
        {isLoading ? (
          <ChartMetricsRowSkeleton
            alwaysRow
            gap={16}
            count={HPA_METRIC_KEYS.length}
            testId="hpa-charts-metrics-skeleton"
          />
        ) : workload ? (
          <ChartMetricsRow alwaysRow alignItems="center" gap={16}>
            <InventoryResourceMetricBox
              testId="current-pods"
              dataKey="currentPods"
              value={workload.podCount}
              unit="PODS"
              colorMap={colorMap}
              titleMap={titleMap}
              size="s"
              roundBy={0}
              decimals={2}
            />
            <InventoryResourceMetricBox
              testId="min-pods"
              dataKey="podCountMin"
              value={metrics?.pods.podCountMin}
              unit="PODS"
              colorMap={colorMap}
              titleMap={titleMap}
              size="s"
              roundBy={0}
              decimals={2}
            />
            <InventoryResourceMetricBox
              testId="max-pods"
              dataKey="podCountMax"
              value={metrics?.pods.podCountMax}
              unit="PODS"
              colorMap={colorMap}
              titleMap={titleMap}
              size="s"
              roundBy={0}
              decimals={2}
            />
            <InventoryResourceMetricBox
              testId="min-cpu"
              dataKey="cpuCoresMin"
              value={metrics?.cpu.totalCpuCoresMin}
              unit="CPU"
              colorMap={colorMap}
              titleMap={titleMap}
              size="s"
              roundBy={2}
            />
            <InventoryResourceMetricBox
              testId="max-cpu"
              dataKey="cpuCoresMax"
              value={metrics?.cpu.totalCpuCoresMax}
              unit="CPU"
              colorMap={colorMap}
              titleMap={titleMap}
              size="s"
              roundBy={2}
            />
          </ChartMetricsRow>
        ) : null}
        <HpaChartsContent
          podsChartData={podsChartData}
          cpuChartData={cpuChartData}
          podsAnalyzeWithOpsPilot={podsAnalyzeWithOpsPilot}
          cpuAnalyzeWithOpsPilot={cpuAnalyzeWithOpsPilot}
          timeZone={timeRangeSelection.timeZone}
          isLoading={isLoading}
        />
      </Stack>
    </WidgetLayout>
  );
};
